import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { Send, MoreVertical, User, Zap } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ConversationsProps {
  conversationId: string | null;
  onRefresh: () => void;
}

export const Conversations: React.FC<ConversationsProps> = ({
  conversationId,
  onRefresh,
}) => {
  const { t } = useTranslation('conversation');
  const [conversation, setConversation] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [messageText, setMessageText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (conversationId) {
      fetchConversation();
      fetchMessages();
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversation = async () => {
    if (!conversationId) return;
    try {
      const response = await apiService.getConversation(conversationId);
      setConversation(response.data);
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
    }
  };

  const fetchMessages = async () => {
    if (!conversationId) return;
    setIsLoading(true);
    try {
      const response = await apiService.getMessages(conversationId);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !conversationId || isSending) return;

    setIsSending(true);
    try {
      await apiService.sendMessage(conversationId, messageText, 'text');
      setMessageText('');
      // Refresh messages
      await fetchMessages();
      onRefresh();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert(t('sendFailed'));
    } finally {
      setIsSending(false);
    }
  };

  if (!conversationId) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">💬</div>
        <div className="empty-state-title">{t('noConversationSelected')}</div>
        <div className="empty-state-description">
          {t('selectToChat')}
        </div>
      </div>
    );
  }

  if (isLoading && !conversation) {
    return <div className="loading-spinner">{t('loadingConversation')}</div>;
  }

  return (
    <div className="message-thread">
      {/* Conversation Header */}
      <div
        style={{
          background: '#fff',
          borderBottom: '1px solid #e5e5ea',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: '#3390ec',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
          >
            <User size={20} />
          </div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 600 }}>
              {conversation?.customer_name || t('customer')}
            </div>
            <div style={{ fontSize: '12px', color: '#8e8e93' }}>
              {conversation?.platform} • {conversation?.status}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            style={{
              background: 'transparent',
              border: '1px solid #e5e5ea',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
            title="Trigger Scenario"
          >
            <Zap size={16} />
            <span style={{ fontSize: '14px' }}>{t('scenario')}</span>
          </button>
          <button
            style={{
              background: 'transparent',
              border: '1px solid #e5e5ea',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
            }}
            title="More"
          >
            <MoreVertical size={16} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="message-list">
        {messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">✉️</div>
            <div className="empty-state-title">{t('noMessages')}</div>
            <div className="empty-state-description">
              {t('startConversation')}
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`message-item ${
                message.sender === 'bot' || message.sender === 'agent'
                  ? 'outgoing'
                  : ''
              }`}
            >
              <div className="message-bubble">
                <div>{message.content}</div>
                <div
                  style={{
                    fontSize: '11px',
                    marginTop: '4px',
                    opacity: 0.7,
                  }}
                >
                  {formatDistanceToNow(new Date(message.created_at), {
                    addSuffix: true,
                  })}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="message-input-area">
        <input
          type="text"
          className="message-input"
          placeholder={t('typeMessage')}
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          disabled={isSending}
        />
        <button
          onClick={handleSendMessage}
          disabled={!messageText.trim() || isSending}
          style={{
            background: messageText.trim() ? '#3390ec' : '#e5e5ea',
            color: messageText.trim() ? '#fff' : '#8e8e93',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: messageText.trim() ? 'pointer' : 'not-allowed',
          }}
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};
